import React from "react";
import ReactDOM from "react-dom";
import { useDrop, money } from "@/pages/public/DropBuilderCtx";
import { usePublicData, mediaUrl } from "@/pages/public/PublicLayout";
import { useBodyLock } from "@/pages/public/useBodyLock";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* ====================================================================
   Build-Your-Drop · Floating cart pill
   Bottom-right glass pill that appears once the cart has items.
   Tapping it opens the slide-out drawer.
   ==================================================================== */
export function DropPill() {
  const { count, subtotal, isOpen, toggle, buildType } = useDrop();
  if (count === 0) return null;

  return (
    <button
      type="button"
      data-testid="drop-pill"
      onClick={toggle}
      className="drop-pill"
      aria-label={`Open your drop · ${count} items · ${money(subtotal)}`}
    >
      <span className="drop-pill__glow" aria-hidden="true" />
      <span className="drop-pill__inner">
        <span className="drop-pill__icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7h18l-1.4 11.2A2 2 0 0 1 17.6 20H6.4a2 2 0 0 1-2-1.8L3 7z" />
            <path d="M8 7V5a4 4 0 0 1 8 0v2" />
          </svg>
        </span>
        <span className="drop-pill__text">
          <span className="drop-pill__type">{buildType}</span>
          <span className="drop-pill__meta">{count} {count === 1 ? "item" : "items"} · {money(subtotal)}</span>
        </span>
        <span className="drop-pill__chev" aria-hidden="true">{isOpen ? "↓" : "↑"}</span>
      </span>
    </button>
  );
}

/* ====================================================================
   Build-Your-Drop · Nav cart badge
   Small icon for the top nav (next to "Members Open" pill). Shows the
   count as a small accent dot, opens the drawer.
   ==================================================================== */
export function DropNavBadge() {
  const { count, toggle } = useDrop();
  return (
    <button
      type="button"
      data-testid="drop-nav-btn"
      onClick={toggle}
      className="drop-nav-btn"
      aria-label={count ? `Your drop · ${count} items` : "Open drop builder"}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7h18l-1.4 11.2A2 2 0 0 1 17.6 20H6.4a2 2 0 0 1-2-1.8L3 7z" />
        <path d="M8 7V5a4 4 0 0 1 8 0v2" />
      </svg>
      {count > 0 && <span className="drop-nav-btn__dot" data-testid="drop-nav-count">{count > 9 ? "9+" : count}</span>}
    </button>
  );
}

/* ====================================================================
   Build-Your-Drop · Slide-out drawer (cinematic glass)
   ==================================================================== */
export function DropDrawer() {
  const { isOpen, close, lines, subtotal, count, updateQty, remove, clear, buildType } = useDrop();
  const { content } = usePublicData() || { content: {} };
  const c = content || {};
  const [submitting, setSubmitting] = React.useState(false);
  const [handle, setHandle] = React.useState("");
  const [note, setNote] = React.useState("");

  // Ref-counted body lock — handles stacking with the product modal cleanly.
  useBodyLock(isOpen);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  async function confirmDrop() {
    if (submitting || lines.length === 0) return;
    setSubmitting(true);

    const payload = {
      lines: lines.map((l) => ({
        product_id: l.product_id,
        product_name: l.product_name,
        tier_key: l.tier_key,
        tier_label: l.tier_label,
        unit_price: l.unit_price,
        qty: l.qty,
      })),
      subtotal,
      build_type: buildType,
      member_handle: handle.trim(),
      member_note: note.trim(),
    };

    let draftId = "";
    try {
      const r = await fetch(`${BACKEND}/api/public/drop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      draftId = d.id || "";
    } catch (_) { /* still send to Telegram even if save fails */ }

    // Auto-format Telegram message
    const lineRows = lines.map((l) =>
      `• ${l.product_name} — ${l.tier_label || l.tier_key} × ${l.qty} → ${money(l.unit_price * l.qty)}`
    ).join("\n");
    const msg = [
      "🌌 NEW DROP REQUEST",
      "",
      lineRows,
      "",
      `BUILD: ${buildType}`,
      `SUBTOTAL: ${money(subtotal)}`,
      draftId ? `REF: ${draftId.slice(0, 8).toUpperCase()}` : "",
      handle ? `MEMBER: ${handle}` : "",
      note ? `NOTE: ${note}` : "",
    ].filter(Boolean).join("\n");

    const tgBase = c.contact_telegram_url || "";
    let target = "";
    if (tgBase && tgBase.includes("t.me/")) {
      // Use t.me deep-link with prefilled text where possible
      target = `${tgBase}${tgBase.includes("?") ? "&" : "?"}text=${encodeURIComponent(msg)}`;
    }
    // Fallback to WhatsApp / SMS if no Telegram url
    if (!target && c.contact_whatsapp_url) {
      target = `${c.contact_whatsapp_url}${c.contact_whatsapp_url.includes("?") ? "&" : "?"}text=${encodeURIComponent(msg)}`;
    }
    if (!target && c.contact_sms_url) {
      target = `${c.contact_sms_url}${c.contact_sms_url.includes("?") ? "&" : "?"}body=${encodeURIComponent(msg)}`;
    }

    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
    } else {
      // Last resort: copy to clipboard
      try { await navigator.clipboard.writeText(msg); } catch (_) {}
      alert("Drop saved. Message copied to clipboard — paste it in Telegram.");
    }

    setSubmitting(false);
    close();
  }

  return ReactDOM.createPortal((
    <div className="drop-drawer-backdrop" data-testid="drop-drawer" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <aside className="drop-drawer" role="dialog" aria-modal="true" aria-labelledby="drop-drawer-title">
        <header className="drop-drawer__head">
          <div className="drop-drawer__head-eye">— Your Drop · {count} {count === 1 ? "item" : "items"}</div>
          <h2 id="drop-drawer-title" className="drop-drawer__title">
            <span>{buildType.split(" ")[0]}</span> <em>{buildType.split(" ").slice(1).join(" ") || "Mix"}</em>
          </h2>
          <button type="button" data-testid="drop-drawer-close" onClick={close} className="drop-drawer__x" aria-label="Close drop builder">✕</button>
        </header>

        <div className="drop-drawer__body">
          {lines.length === 0 ? (
            <div className="drop-drawer__empty">
              <div className="drop-drawer__empty-eye">— Empty Drop</div>
              <h3>Build your private drop.</h3>
              <p>Mix any 8 jars into a custom zip · combine quarters into a half-pound · curate your own rotation. Open any strain and tap a weight to start.</p>
              <button type="button" className="drop-drawer__cta" onClick={close} data-testid="drop-drawer-browse">Browse the menu →</button>
            </div>
          ) : (
            <ul className="drop-drawer__lines">
              {lines.map((l) => (
                <li key={l.id} className="drop-line" data-testid={`drop-line-${l.id}`}>
                  <div className="drop-line__thumb" style={{ backgroundImage: l.img ? `url('${mediaUrl(l.img)}')` : "" }}>
                    {!l.img && <span className="drop-line__thumb-empty" aria-hidden="true">·</span>}
                  </div>
                  <div className="drop-line__info">
                    <div className="drop-line__name">{l.product_name}</div>
                    <div className="drop-line__tier">{l.tier_label || l.tier_key} · {money(l.unit_price)} each</div>
                    <div className="drop-line__qty-row">
                      <button type="button" className="drop-line__qbtn" aria-label="Decrease" data-testid={`drop-line-${l.id}-dec`}
                        onClick={() => updateQty(l.id, (l.qty || 1) - 1)}>−</button>
                      <span className="drop-line__qty" data-testid={`drop-line-${l.id}-qty`}>{l.qty}</span>
                      <button type="button" className="drop-line__qbtn" aria-label="Increase" data-testid={`drop-line-${l.id}-inc`}
                        onClick={() => updateQty(l.id, (l.qty || 1) + 1)}>+</button>
                      <button type="button" className="drop-line__rm" data-testid={`drop-line-${l.id}-rm`}
                        onClick={() => remove(l.id)} aria-label={`Remove ${l.product_name}`}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="drop-line__total">{money((l.unit_price || 0) * (l.qty || 1))}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {lines.length > 0 && (
          <footer className="drop-drawer__foot">
            <label className="drop-drawer__field">
              <span>Telegram @ (optional)</span>
              <input type="text" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@yourhandle" data-testid="drop-drawer-handle" maxLength={40} />
            </label>
            <label className="drop-drawer__field">
              <span>Note (optional)</span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know about your drop?" data-testid="drop-drawer-note" maxLength={400} />
            </label>

            <div className="drop-drawer__sub">
              <span>Subtotal</span>
              <strong data-testid="drop-drawer-subtotal">{money(subtotal)}</strong>
            </div>
            <p className="drop-drawer__legal">Members only · confirm by Telegram · private drop · 21+</p>

            <div className="drop-drawer__actions">
              <button type="button" className="drop-drawer__cancel" onClick={clear} data-testid="drop-drawer-clear">Clear Drop</button>
              <button type="button" className="drop-drawer__confirm" onClick={confirmDrop} disabled={submitting} data-testid="drop-drawer-confirm">
                {submitting ? "Sending…" : "Confirm Drop →"}
              </button>
            </div>
          </footer>
        )}
      </aside>
    </div>
  ), document.body);
}
